import { toast } from "sonner";
import { importTheme } from "@/importers/parsers";
import { cloneTheme, newId, type Theme } from "@/model/theme";
import {
  addSnapshot,
  db,
  deletePreset,
  exportBackup,
  importBackup,
  type PresetRow,
  savePreset,
} from "@/presets/repo";
import { shareUrl } from "@/presets/share";
import { starterTheme } from "@/presets/starters";
import { useEditor } from "@/store/editor";
import { download } from "./dialogs/export";
import { persistDraft } from "./hooks/boot";

const editor = () => useEditor.getState();

/** Saves the draft as its preset (a starter or share link becomes a new preset). */
export async function save(): Promise<void> {
  const { draft } = editor();
  // Starters are read-only templates: saving one creates a new preset.
  const theme = draft.id.startsWith("starter:") ? { ...draft, id: newId() } : draft;
  await savePreset(theme);
  useEditor.setState({ draft: theme, saved: theme });
  await persistDraft();
  toast.success(`Saved “${theme.name}”`);
}

export async function saveAs(): Promise<void> {
  const copy = cloneTheme(editor().draft, `${editor().draft.name} copy`);
  await savePreset(copy);
  editor().open(copy, copy);
  await persistDraft();
  toast.success(`Saved as “${copy.name}”`, { description: "Rename it in the title field." });
}

export async function snapshot(label = "Snapshot"): Promise<void> {
  const { draft } = editor();
  if (!(await db.presets.get(draft.id))) await save();
  await addSnapshot(editor().draft, label);
  toast.success("Snapshot taken", { description: "Find it in Presets → Snapshots." });
}

export function openTheme(theme: Theme, saved: Theme | null = theme): void {
  editor().open(theme, saved);
}

export function openPreset(row: PresetRow): void {
  openTheme(row.theme, row.theme);
}

export function openStarter(id: string): void {
  const t = starterTheme(id);
  if (t) openTheme(t, null);
}

export async function removePreset(id: string): Promise<void> {
  const row = await db.presets.get(id);
  await deletePreset(id);
  if (editor().draft.id === id) useEditor.setState({ saved: null });
  toast(`Deleted “${row?.name ?? "preset"}”`, {
    action: row
      ? {
          label: "Undo",
          onClick: () => {
            void savePreset(row.theme);
          },
        }
      : undefined,
  });
}

export async function share(): Promise<void> {
  const url = shareUrl(editor().draft);
  await navigator.clipboard.writeText(url);
  toast.success("Share link copied", {
    description: "The whole theme is in the link; nothing is uploaded.",
  });
}

export async function importFile(file: File): Promise<void> {
  const text = await file.text();
  try {
    if (/"kind"\s*:\s*"(themes|hue)-backup"/.test(text.slice(0, 200))) {
      const n = await importBackup(JSON.parse(text));
      toast.success(`Restored ${n} preset${n === 1 ? "" : "s"} from backup`);
      return;
    }
    const { theme, format } = importTheme(text, file.name);
    openTheme(theme, null);
    toast.success(`Imported “${theme.name}”`, { description: `${format} format · not saved yet` });
  } catch (err) {
    toast.error(`Could not import ${file.name}`, { description: (err as Error).message });
  }
}

export async function backup(): Promise<void> {
  const data = await exportBackup();
  download(
    `themes-backup-${new Date().toISOString().slice(0, 10)}.json`,
    JSON.stringify(data, null, 2),
    "application/json",
  );
  toast.success(`Backed up ${data.presets.length} preset${data.presets.length === 1 ? "" : "s"}`);
}

export function pinAsB(theme: Theme, label: string): void {
  editor().setCompare({ theme, label });
  toast(`Pinned “${label}” as B`, { description: "Press \\ to flip between A and B." });
}

export function pinCurrentAsB(): void {
  pinAsB(structuredClone(editor().draft), `${editor().draft.name} (pinned)`);
}

export function pickFile(
  accept = ".json,.yaml,.yml,.toml,.conf,.itermcolors,*",
): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}
